import type { Language } from '@eformulieren/engine'
import { Person } from '@eformulieren/model'
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react'
import { isNwoEmployee } from '../document/presence.js'
import { useSnapshot } from '../document/snapshot.js'
import { colorFor } from './attribution.js'
import { NWO_EMPLOYEE_COLOR, nwoEmployeeLabel } from './employee.js'
import { inLanguage } from '../language/text.js'

/**
 * Wie er nu in deze sectie werkt (R-72, R-188).
 *
 * Overgenomen uit de CitatenPOC (`src/collab/PresenceBar.tsx`), met drie verschillen.
 *
 * **1. De naam is decoratie en nooit identiteit** (beveiligingsanalyse PortaalPOC). Awareness is
 * vluchtige staat die elke client zelf schrijft en die niet in het document landt. Sinds de
 * beveiligingsronde van 1 november 2026 bindt de pod haar wel aan de verbinding: één clientId per
 * verbinding, met de naam uit het token (`apps/runtime/src/boundAwareness.ts`). Dat is genoeg voor
 * het doel ("er werkt nog iemand mee"), en het blijft nadrukkelijk niet genoeg voor iets wat op
 * autoriteit rust: het is een momentopname die niemand bewaart. Waar het wél op aankomt, staat de
 * auteur uit het token in het document: de herkomst per veld (`herkomst.ts`), de auteursmap
 * (`auteurs.ts`) en de opmerkingen (`draden.ts`).
 *
 * **2. Er is geen knop om je naam te wijzigen.** In de POC kiest de gebruiker zijn eigen naam en
 * herlaadt de pagina. Hier komt de naam van de gastheer, uit het token; een veld om hem te
 * veranderen zou precies de bewering versterken die hierboven geen autoriteit heeft.
 *
 * **3. De kleur wordt berekend en niet meegestuurd.** `colorFor` geeft dezelfde persoon overal
 * dezelfde kleur, en de kleur is nooit de enige drager: de naam staat er als tekst bij (R-139).
 * Bij veel medeschrijvers herhaalt de kleur en blijft de naam onderscheiden.
 *
 * **Een medewerker van NWO staat er zonder naam** (R-66a, B6, opdracht 8). Op een document dat de
 * externe kant kan lezen, stuurt de pod voor een deelnemer van de interne kant een merkteken in
 * plaats van een naam; de balk zet daar het label in de taal van de lezer bij en een vaste kleur.
 */

const LABEL = { nl: 'Wie werken hier nu', en: 'Who is working here now' }
const SELF = { nl: 'u', en: 'you' }
const SELF_ALONE = { nl: 'U', en: 'You' }
const PARTICIPANT = { nl: 'Deelnemer', en: 'Participant' }

/**
 * **Nooit een persoonssleutel op het scherm** (ontwerp van het invulscherm, 29 oktober 2026).
 *
 * De pagina zet in de awareness wat zij van de persoon weet, en dat is de sleutel van R-155e
 * (`portaal:00000000-…`): dit product kent geen namen (R-154). Zo'n sleutel is geen naam en hoort
 * niet in beeld; hij zegt een lezer niets en is wel een gegeven over een persoon. Een sleutel wordt
 * daarom "U" voor de lezer zelf, en voor een ander de naam die de gastheer geeft (`nameOf`) of
 * "Deelnemer" met een volgnummer. Een echte naam, als een gastheer die ooit in de awareness zet,
 * blijft staan.
 */
export function isPersonKey(name: string): boolean {
  return Person.safeParse(name).success
}

/** Hoe één aanwezige in beeld heet, zonder ooit een persoonssleutel te tonen. */
export function presentName(present: Present, language: Language, nameOf?: (person: string) => string): string {
  if (present.anoniem) return present.zelf ? `${present.naam} (${inLanguage(SELF, language)})` : present.naam
  if (isPersonKey(present.naam)) {
    if (present.zelf) return inLanguage(SELF_ALONE, language)
    return nameOf?.(present.naam) ?? inLanguage(PARTICIPANT, language)
  }
  return present.zelf ? `${present.naam} (${inLanguage(SELF, language)})` : present.naam
}

/**
 * Een volgnummer per persoonssleutel, over alle secties heen: "Deelnemer 1", "Deelnemer 2".
 *
 * Gesorteerd op de sleutel, zodat het nummer niet afhangt van wie het eerst verbond en in elke
 * sectie en in de kopbalk dezelfde persoon hetzelfde nummer heeft.
 */
export function participantNames(persons: Iterable<string>, language: Language): (person: string) => string {
  const numbered = new Map([...new Set(persons)].sort().map((person, index) => [person, index + 1]))
  return (person) => `${inLanguage(PARTICIPANT, language)} ${String(numbered.get(person) ?? numbered.size + 1)}`
}

/** Wat een presence-balk van de awareness nodig heeft, en verder niets. */
export interface PresenceSource {
  clientID: number
  getStates(): Map<number, Record<string, unknown>>
  on(event: 'change', hook: () => void): void
  off(event: 'change', hook: () => void): void
  /**
   * Een veld van de eigen staat zetten, zoals `Awareness` van y-protocols dat kan: de schil zet er
   * `focus` mee in de sectie waar de lezer werkt (G-18). Zonder deze methode zegt niemand waar hij is.
   */
  setLocalStateField?(field: string, value: unknown): void
}

export interface Present {
  readonly clientId: number
  /** De naam, of bij een medewerker van NWO het label in de taal van wie kijkt. */
  readonly naam: string
  readonly zelf: boolean
  /** Een medewerker van NWO: geen naam, en een vaste kleur. */
  readonly anoniem: boolean
  /** Of deze deelnemer nu in deze sectie werkt: de focus staat erin (`focus` in de awareness, G-18). */
  readonly focus: boolean
  /**
   * In welk veld van deze sectie hij werkt: de id van het veld met zijn focus (`veld` in de awareness,
   * `FieldPresence`). De pod laat alleen de id van een veld van deze sectie door (`boundAwareness.ts`).
   */
  readonly veld?: string
}

/** `veld` uit een staat, alleen als tekst; de pod heeft al nagekeken dat het een veld van de sectie is. */
function fieldOf(state: Record<string, unknown>): { veld?: string } {
  return typeof state.veld === 'string' && state.veld !== '' ? { veld: state.veld } : {}
}

/**
 * Wie de awareness nu noemt, in een vaste orde: jij eerst, daarna op naam.
 *
 * Een vaste orde omdat een lijst die per hertekening van plaats wisselt niet te lezen is, en
 * omdat een schermlezer hem anders elke keer opnieuw voorleest.
 */
export function presentUsersIn(source: PresenceSource, language: Language): readonly Present[] {
  const out: Present[] = []
  for (const [clientId, state] of source.getStates()) {
    const self = clientId === source.clientID
    if (isNwoEmployee(state.user)) {
      out.push({ clientId, naam: nwoEmployeeLabel(language), zelf: self, anoniem: true, focus: state.focus === true, ...fieldOf(state) })
      continue
    }
    const user = state.user as { name?: unknown } | undefined
    const name = typeof user?.name === 'string' ? user.name : undefined
    if (name === undefined || name === '') continue
    out.push({ clientId, naam: name, zelf: self, anoniem: false, focus: state.focus === true, ...fieldOf(state) })
  }
  return out.sort((a, b) => (a.zelf === b.zelf ? a.naam.localeCompare(b.naam) : a.zelf ? -1 : 1))
}

export interface PresenceBarProps {
  readonly awareness: PresenceSource | undefined
  readonly taal: Language
  /** Hoe een ander met een persoonssleutel heet; zie {@link presentName}. */
  readonly nameOf?: (person: string) => string
  /** De eigen persoonssleutel: een tweede venster van dezelfde persoon is nooit een ander (G-18). */
  readonly ik?: string
}

const WORKS_HERE = { nl: 'werkt hier', en: 'is working here' }
const WORK_HERE = { nl: 'werken hier', en: 'are working here' }

/**
 * "A werkt hier", "A en B werken hier": de tekst bij de sectie en bij het veld (`FieldPresence`), zodat
 * dezelfde persoon op beide plekken met dezelfde naam en dezelfde woorden staat.
 */
export function worksHereText(names: readonly string[], language: Language): string {
  return `${joined(names, language)} ${inLanguage(names.length === 1 && !names[0]?.endsWith(')') ? WORKS_HERE : WORK_HERE, language)}`
}

/**
 * Wie anders dan de lezer hier werkt, één keer per persoon: niet de lezer in dit venster, en niet de
 * lezer in een ander venster (dezelfde persoonssleutel, G-18). Een medewerker van NWO heeft geen sleutel
 * in de awareness (R-66a) en telt per client.
 */
export function othersHere(present: readonly Present[], here: (one: Present) => boolean, me: string | undefined): readonly Present[] {
  return onePerPerson(present.filter((one) => !one.zelf && here(one) && (me === undefined || one.anoniem || one.naam !== me)))
}

/** De kleur van de stip bij een aanwezige: dezelfde als zijn cursor (`caretUser`, `boundAwareness.ts`). */
export function presenceColor(present: Present): string {
  // De donkere variant: de lichte is de kleur op 15 procent dekking en bedoeld als achtergrond, en als
  // stip is die vrijwel onzichtbaar. Zie `ThreadCard.tsx`.
  return (present.anoniem ? NWO_EMPLOYEE_COLOR : colorFor(present.naam)).dark
}

/** "A", "A en B", "A, B en C". */
function joined(names: readonly string[], language: Language): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} ${language === 'nl' ? 'en' : 'and'} ${names[names.length - 1] ?? ''}`
}

/**
 * Eén regel per persoon: dezelfde ander in twee vensters heeft twee client-id's maar één sleutel, en
 * telt in de kop ook één keer ("U en 1 ander", `summarizePresence`). Een medewerker van NWO heeft geen
 * sleutel in de awareness (R-66a) en telt per client; meer is niet te weten.
 */
function onePerPerson(present: readonly Present[]): readonly Present[] {
  const seen = new Set<string>()
  return present.filter((one) => {
    if (one.anoniem) return true
    if (seen.has(one.naam)) return false
    seen.add(one.naam)
    return true
  })
}

/** Dezelfde naam twee keer (twee medewerkers van NWO) als één naam met het aantal erbij. */
export function counted(names: readonly string[]): string[] {
  const count = new Map<string, number>()
  for (const name of names) count.set(name, (count.get(name) ?? 0) + 1)
  return [...count].map(([name, times]) => (times === 1 ? name : `${name} (${String(times)})`))
}

/**
 * Wie er nu in deze sectie werkt, onder de kop (R-72, audit delen G-18, mockup 06).
 *
 * **Alleen wie hier de focus heeft, en nooit uzelf.** Elke deelnemer heeft alle secties open (een
 * document per sectie, 9.2), dus iedereen staat in elke awareness; wie er alleen open heeft, werkt hier
 * niet. Wie de focus in deze sectie heeft, zet `focus` in zijn staat (`FillForm`). Een tweede venster
 * van de lezer zelf heeft dezelfde persoonssleutel en telt niet als een ander. Is er niemand, dan staat
 * er niets: dat de lezer alleen is zegt de kopbalk één keer.
 */
export function PresenceBar({ awareness, taal: language, nameOf, ik: me }: PresenceBarProps): ReactNode {
  const subscribe = useCallback(
    (hook: () => void) => {
      if (awareness === undefined) return () => undefined
      awareness.on('change', hook)
      return () => awareness.off('change', hook)
    },
    [awareness],
  )
  const read = useCallback(
    () => (awareness === undefined ? EMPTY : presentUsersIn(awareness, language)),
    [awareness, language],
  )
  // `useSnapshot` en niet `useSyncExternalStore` rechtstreeks: `presentUsersIn` maakt per
  // aanroep een nieuwe lijst, en dan hertekent React eindeloos. Zie de kop daar.
  const presentUsers = useSnapshot(subscribe, read)

  if (awareness === undefined) return null
  const here = othersHere(presentUsers, (present) => present.focus, me)
  if (here.length === 0) return null
  const names = counted(here.map((present) => presentName(present, language, nameOf)))
  return (
    <p className="ef-presence" aria-label={inLanguage(LABEL, language)}>
      <span className="ef-presence__stippen" aria-hidden="true">
        {here.map((present) => (
          <span
            key={present.clientId}
            className="ef-presence__stip"
            style={{ '--ef-presencekleur': presenceColor(present) } as Record<string, string>}
          />
        ))}
      </span>
      <span className="ef-presence__tekst">
        {worksHereText(names, language)}
      </span>
    </p>
  )
}

/** Eén lege lijst; een verse per lezing zou eindeloos hertekenen (zie `useThreads`). */
const EMPTY: readonly Present[] = []

const SUMMARY_LABEL = { nl: 'Wie werken mee', en: 'Who is working on this' }
const ONLY_YOU = { nl: 'Alleen u', en: 'Only you' }
const YOU = { nl: 'U', en: 'You' }
const YOU_ELSEWHERE = { nl: 'U, in een ander venster', en: 'You, in another window' }
const ANNOUNCE_TOGGLE = { nl: 'Meld wie binnenkomt of weggaat', en: 'Announce who joins or leaves' }
const ANNOUNCE_KEY = 'ef-aanwezig-meldingen'

/** Eén sectie met haar awareness, voor de lijst in de kop: daar staat bij elke persoon waar hij werkt. */
export interface SectionPresence {
  readonly sectieId: string
  /** De titel van de sectie in de taal van de lezer. */
  readonly titel: string
  readonly source: PresenceSource
  /** Het label van een veld van deze sectie in de taal van de lezer, voor "in Overzicht, bij Titel". */
  readonly veldTitel?: (veldId: string) => string | undefined
}

/**
 * Wie er nu in het formulier werkt, over alle secties heen, voor de kopbalk (R-72, audit delen G-18,
 * G-19, G-22, mockup 06, analyse 4.5).
 *
 * Elke sectie heeft een eigen awareness (een document per sectie, 9.2), dus dezelfde persoon staat in
 * elke sectie met een eigen client-id. Hier telt hij één keer: op zijn sleutel. Een medewerker van NWO
 * heeft geen sleutel in de awareness (R-66a); die telt zo vaak als hij in één sectie het vaakst
 * voorkomt, want meer is niet te weten. De stip is versiering; de namen staan er als tekst (R-139).
 */
export interface PresenceSummaryProps {
  readonly sources: readonly SectionPresence[]
  readonly taal: Language
  readonly nameOf?: (person: string) => string
  /** De eigen persoonssleutel: een tweede venster van de lezer is "U, in een ander venster" (G-18). */
  readonly ik?: string
}

/** Waar iemand nu werkt: de sectie, en het veld als de focus in een veld staat. */
export interface Place {
  readonly sectie: string
  readonly veld?: string
}

/** Eén regel in de lijst van de kop: wie, waar, en of het de lezer zelf is. */
export interface Summarized {
  readonly sleutel: string
  readonly naam: string
  /** De donkere variant van de kleur van zijn cursor: de rand van zijn vak. */
  readonly kleur: string
  /** De lichte variant: het vlak van zijn vak, zoals het label bij zijn cursor. */
  readonly tint: string
  /** De lezer in dit venster. */
  readonly zelf: boolean
  /** De lezer in een ander venster. */
  readonly elders: boolean
  /** De titels van de secties waar hij nu de focus heeft. */
  readonly secties: readonly string[]
  /** Waar hij nu werkt, per sectie met het veld erbij (`veld` in de awareness, `FieldPresence`). */
  readonly plekken: readonly Place[]
}

const ARTICLES = new Set(['de', 'het', 'een', 'the', 'a', 'an'])

/**
 * De beginletter in het vak van een persoon: "De aanvrager" wordt A, "aanvrager@voorbeeld.nl" A, "U" U.
 * Versiering naast de naam (R-139), en nooit uit een persoonssleutel, want de naam is er al geen.
 */
export function initialOf(name: string): string {
  const words = name.split(/[\s@.,()]+/u).filter((word) => word !== '')
  const word = words.find((one, index) => index === words.length - 1 || !ARTICLES.has(one.toLocaleLowerCase('nl'))) ?? ''
  const letter = [...word].find((character) => /[\p{L}\p{N}]/u.test(character))
  return letter === undefined ? '?' : letter.toLocaleUpperCase('nl')
}

export function summarizePresence(
  sources: readonly SectionPresence[],
  language: Language,
  nameOf?: (person: string) => string,
  me?: string,
): readonly Summarized[] {
  type Base = { naam: string; kleur: string; tint: string; zelf: boolean; elders: boolean }
  const people = new Map<string, Base & { secties: string[]; plekken: Place[] }>()
  const add = (key: string, base: Base, place: Place | undefined): void => {
    const known = people.get(key) ?? { ...base, secties: [], plekken: [] }
    if (place !== undefined) {
      if (!known.secties.includes(place.sectie)) known.secties.push(place.sectie)
      if (!known.plekken.some((one) => one.sectie === place.sectie && one.veld === place.veld)) known.plekken.push(place)
    }
    people.set(key, known)
  }
  const colored = (person: string): { kleur: string; tint: string } => {
    const pair = colorFor(person)
    return { kleur: pair.dark, tint: pair.light }
  }
  let employees = 0
  let ownEmployee: Place | undefined | null = null
  const focusedEmployees: Place[] = []
  for (const { titel, source, veldTitel } of sources) {
    const present = presentUsersIn(source, language)
    employees = Math.max(employees, present.filter((one) => one.anoniem && !one.zelf).length)
    for (const one of present) {
      const field = one.veld === undefined ? undefined : veldTitel?.(one.veld)
      const where: Place | undefined = one.focus ? { sectie: titel, ...(field === undefined ? {} : { veld: field }) } : undefined
      if (one.anoniem) {
        if (one.zelf) ownEmployee = where ?? ownEmployee ?? undefined
        else if (where !== undefined) focusedEmployees.push(where)
        continue
      }
      if (one.zelf) {
        add(`zelf`, { naam: inLanguage(YOU, language), ...colored(one.naam), zelf: true, elders: false }, where)
        continue
      }
      if (me !== undefined ? one.naam === me : false) {
        add(`elders`, { naam: inLanguage(YOU_ELSEWHERE, language), ...colored(one.naam), zelf: false, elders: true }, where)
        continue
      }
      add(one.naam, { naam: presentName(one, language, nameOf), ...colored(one.naam), zelf: false, elders: false }, where)
    }
  }
  const out: Summarized[] = [...people].map(([key, one]) => ({ sleutel: key, ...one }))
  if (ownEmployee !== null) {
    out.push({
      sleutel: 'nwo-zelf',
      naam: `${nwoEmployeeLabel(language)} (${inLanguage(SELF, language)})`,
      kleur: NWO_EMPLOYEE_COLOR.dark,
      tint: NWO_EMPLOYEE_COLOR.light,
      zelf: true,
      elders: false,
      secties: ownEmployee === undefined ? [] : [ownEmployee.sectie],
      plekken: ownEmployee === undefined ? [] : [ownEmployee],
    })
  }
  for (let index = 0; index < Math.max(employees, focusedEmployees.length); index += 1) {
    const focused = focusedEmployees[index]
    out.push({
      sleutel: `nwo-${String(index)}`,
      naam: nwoEmployeeLabel(language),
      kleur: NWO_EMPLOYEE_COLOR.dark,
      tint: NWO_EMPLOYEE_COLOR.light,
      zelf: false,
      elders: false,
      secties: focused === undefined ? [] : [focused.sectie],
      plekken: focused === undefined ? [] : [focused],
    })
  }
  const rank = (one: Summarized): number => (one.zelf ? 0 : one.elders ? 1 : 2)
  return out.sort((a, b) => rank(a) - rank(b) || a.naam.localeCompare(b.naam))
}

/** "U en 1 ander", "U en 3 anderen". */
function summaryText(others: number, elsewhere: boolean, language: Language): string {
  if (others === 0) return inLanguage(elsewhere ? YOU_ELSEWHERE : ONLY_YOU, language)
  return language === 'nl'
    ? `U en ${String(others)} ${others === 1 ? 'ander' : 'anderen'}`
    : `You and ${String(others)} ${others === 1 ? 'other' : 'others'}`
}

const STACK = 4

/** "in Overzicht, bij Titel van het project", "in Overzicht en Bijlagen". */
function placesText(places: readonly Place[], language: Language): string {
  const parts = places.map((place) =>
    place.veld === undefined ? place.sectie : `${place.sectie}, ${language === 'nl' ? 'bij' : 'at'} ${place.veld}`,
  )
  return `in ${joined(parts, language)}`
}

/** Het vak van één persoon: zijn beginletter in de kleur van zijn cursor; de naam staat ernaast (R-139). */
function Chip({ one }: { readonly one: Summarized }): ReactNode {
  return (
    <span
      className="ef-aanwezig__chip"
      aria-hidden="true"
      style={{ '--ef-presencekleur': one.kleur, '--ef-presencetint': one.tint } as Record<string, string>}
    >
      {initialOf(one.naam)}
    </span>
  )
}

function announceOn(): boolean {
  try {
    return window.localStorage.getItem(ANNOUNCE_KEY) !== 'uit'
  } catch {
    return true
  }
}

export function PresenceSummary({ sources, taal: language, nameOf, ik: me }: PresenceSummaryProps): ReactNode {
  const subscribe = useCallback(
    (hook: () => void) => {
      for (const { source } of sources) source.on('change', hook)
      return () => {
        for (const { source } of sources) source.off('change', hook)
      }
    },
    [sources],
  )
  const read = useCallback(() => summarizePresence(sources, language, nameOf, me), [sources, language, nameOf, me])
  const people = useSnapshot(subscribe, read)
  const [open, setOpen] = useState(false)
  const [announce, setAnnounce] = useState(announceOn)
  const [announcement, setAnnouncement] = useState('')
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listId = useId()

  /*
   * Eén beleefde melding per persoon die binnenkomt of weggaat (analyse 4.5, zoals Google): niet per
   * sectie, want bij twaalf secties is dat twaalf keer hetzelfde. De eerste lezing is de beginstand en
   * wordt niet gemeld. Uit te zetten met het vinkje in de lijst.
   */
  const others = people.filter((one) => !one.zelf && !one.elders)
  const othersKey = others.map((one) => `${one.sleutel}\u0000${one.naam}`).join('\u0001')
  const previous = useRef<ReadonlyMap<string, string> | undefined>(undefined)
  const announceRef = useRef(announce)
  announceRef.current = announce
  useEffect(() => {
    const now = new Map(othersKey === '' ? [] : othersKey.split('\u0001').map((entry) => entry.split('\u0000') as [string, string]))
    const before = previous.current
    previous.current = now
    if (before === undefined || !announceRef.current) return
    const came = [...now].filter(([key]) => !before.has(key)).map(([, name]) => name)
    const went = [...before].filter(([key]) => !now.has(key)).map(([, name]) => name)
    const said = [
      ...came.map((name) => (language === 'nl' ? `${name} werkt nu mee.` : `${name} has joined.`)),
      ...went.map((name) => (language === 'nl' ? `${name} is weggegaan.` : `${name} has left.`)),
    ]
    if (said.length > 0) setAnnouncement(said.join(' '))
  }, [othersKey, language])

  const region = (
    <span className="ef-sr-only" aria-live="polite" data-proef="aanwezig-melding">
      {announcement}
    </span>
  )
  // De regio staat er altijd en op dezelfde plek, ook als de rest verandert: een live regio die er
  // met inhoud en al bij komt, leest een schermlezer niet voor.
  if (sources.length === 0) return <>{null}{region}</>
  const elsewhere = people.some((one) => one.elders)
  const text = summaryText(others.length, elsewhere, language)
  // Hoogstens vier vakken in de knop, en daarna "+2": de tekst ernaast zegt het aantal.
  const stacked = people.length > STACK ? people.slice(0, STACK - 1) : people
  const dots = (
    <span className="ef-aanwezig__stapel" aria-hidden="true">
      {stacked.map((one) => (
        <Chip key={one.sleutel} one={one} />
      ))}
      {people.length > STACK ? <span className="ef-aanwezig__chip ef-aanwezig__meer">{`+${String(people.length - stacked.length)}`}</span> : null}
    </span>
  )
  if (others.length === 0 && !elsewhere) {
    return (
      <>
        <p className="ef-aanwezig" aria-label={inLanguage(SUMMARY_LABEL, language)}>
          {dots}
          <span className="ef-aanwezig__tekst">{text}</span>
        </p>
        {region}
      </>
    )
  }
  const close = (): void => {
    setOpen(false)
    buttonRef.current?.focus()
  }
  return (
    <>
    <div
      className="ef-aanwezig ef-aanwezig--knop"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation()
          close()
        }
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className="ef-aanwezig__knop"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={
          language === 'nl'
            ? `${text} ${others.length === 0 ? 'werkt' : 'werken'} in dit formulier`
            : `${text} ${others.length === 0 ? 'is' : 'are'} working on this form`
        }
        onClick={() => setOpen((shown) => !shown)}
        data-proef="aanwezig"
      >
        {dots}
        <span className="ef-aanwezig__tekst">{text}</span>
      </button>
      <div className="ef-aanwezig__lijstvak" id={listId} hidden={!open}>
        {open ? (
          <>
            <ul className="ef-aanwezig__lijst" aria-label={inLanguage(SUMMARY_LABEL, language)}>
              {people.map((one) => (
                <li key={one.sleutel} className="ef-aanwezig__regel">
                  <Chip one={one} />
                  <span className="ef-aanwezig__wie">
                    <span className="ef-aanwezig__naam">{one.naam}</span>
                    {one.plekken.length === 0 ? null : (
                      <>
                        {/* Voorgelezen als één zin, "De aanvrager, in Overzicht"; in beeld op een tweede, stillere regel. */}
                        <span className="ef-sr-only">, </span>
                        <span className="ef-aanwezig__waar">{placesText(one.plekken, language)}</span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <label className="ef-aanwezig__melden">
              <input
                type="checkbox"
                checked={announce}
                onChange={(event) => {
                  const on = event.target.checked
                  setAnnounce(on)
                  try {
                    window.localStorage.setItem(ANNOUNCE_KEY, on ? 'aan' : 'uit')
                  } catch {
                    // Geen opslag: dan alleen voor deze pagina.
                  }
                }}
              />
              <span>{inLanguage(ANNOUNCE_TOGGLE, language)}</span>
            </label>
          </>
        ) : null}
      </div>
    </div>
    {region}
    </>
  )
}
